/**
 * Registers Seeq-specific recognizers and scrubbers with the canonical telemetry
 * serializer (`@seeqdev/telemetry`). Registration happens via an explicit call to
 * {@link registerSeeqTelemetryErrorHandlers} at app bootstrap (`index.tsx`), not as
 * an import side effect; the idempotency guard makes calling it more than once safe.
 * A test that needs the Seeq recognizers/scrubbers registered (rather than mocking
 * `@seeqdev/telemetry` outright) must call {@link registerSeeqTelemetryErrorHandlers}
 * itself, e.g. from a `beforeAll`.
 *
 * Test note: a test that fully mocks `@seeqdev/telemetry` (e.g. `jest.mock('@seeqdev/telemetry')`)
 * must spread the real module in via `jest.requireActual` (`{ ...jest.requireActual('@seeqdev/telemetry'), ... }`)
 * so that `registerErrorRecognizer`/`registerLogScrubber` still exist as callable functions —
 * otherwise calling {@link registerSeeqTelemetryErrorHandlers} throws at registration time.
 */
import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { getCsrfToken } from '@/utilities/auth.utilities';
import { registerErrorRecognizer, registerLogScrubber } from '@seeqdev/telemetry';
import type { AxiosResponse } from 'axios';

interface HttpConfigLike {
  url?: string;
  method?: string;
  headers: Record<string, unknown>;
}

/**
 * Test if the argument looks like it is an http config object
 */
function isHttpConfig(config: unknown): config is HttpConfigLike {
  return _.has(config, 'url') && _.has(config, 'method') && _.has(config, 'headers');
}

/**
 * Test if the argument looks like it is an http response object
 */
function isHttpResponse(response: unknown): response is AxiosResponse<unknown> {
  return _.has(response, 'status') && isHttpConfig(_.get(response as object, 'config'));
}

/**
 * Test if the argument looks like it is a websocket response with a status message
 */
function isWebsocketStatusMessage(response: unknown): response is { channelId: unknown; statusMessage: unknown } {
  return _.has(response, 'channelId') && _.has(response, 'statusMessage');
}

let registered = false;

/**
 * Registers Seeq-specific error recognizers (http responses/configs, websocket
 * status messages) and sensitive-data scrubbers with the canonical telemetry
 * serializer. Runs once on module import; safe to call again.
 */
export function registerSeeqTelemetryErrorHandlers(): void {
  if (registered) {
    return;
  }
  registered = true;

  registerLogScrubber((text) => {
    const token = getCsrfToken();
    if (!token) return text;
    // Scrubbing runs once over the final assembled string, after JSON.stringify
    // has escaped leaf contents — so a token containing a `"` or `\` survives
    // in the output as its JSON-escaped form. Scrub both the raw token (in
    // case it appears unescaped, e.g. outside a JSON leaf) and its escaped
    // form. Use replaceAll: a message can embed the token more than once (e.g.
    // in multiple fields of a serialized object), and every occurrence must be
    // scrubbed.
    const escaped = JSON.stringify(token).slice(1, -1);
    const scrubbed = text.replaceAll(token, '****');
    return escaped === token ? scrubbed : scrubbed.replaceAll(escaped, '****');
  });
  registerLogScrubber((text) =>
    text.replace(new RegExp(`("${SeeqNames.API.Headers.Csrf}"\\s*:\\s*")(.*?)"`, 'g'), '$1****"'),
  );

  registerErrorRecognizer({
    name: 'seeq-http-response',
    test: (value) => _.isPlainObject(value) && isHttpResponse(value),
    format: (value, serialize) => {
      const response = value as AxiosResponse<unknown>;
      const path = _.split(response.config.url ?? '', '?', 1)[0];
      const summary = `${serialize(response.config.method)} ${serialize(path)} ${serialize(
        response.status,
      )} ${serialize(response.statusText)}`;
      const statusMessage = _.get(response, 'data.statusMessage');
      if (!_.isEmpty(statusMessage)) {
        return `${serialize(statusMessage)} (${summary})`;
      } else if (!_.isNil(response.data) && response.status >= 300) {
        return `${serialize(response.data)} (${summary})`;
      }
      return summary;
    },
  });

  registerErrorRecognizer({
    name: 'seeq-http-config',
    test: (value) => _.isPlainObject(value) && isHttpConfig(value),
    format: (value, serialize) => {
      const config = value as HttpConfigLike;
      const path = _.split(config.url ?? '', '?', 1)[0];
      const requestId = config.headers[SeeqNames.API.Headers.RequestId];
      return requestId
        ? `${serialize(config.method)} ${serialize(path)} (${serialize(requestId)})`
        : `${serialize(config.method)} ${serialize(path)}`;
    },
  });

  registerErrorRecognizer({
    name: 'seeq-websocket-status',
    test: (value) => _.isPlainObject(value) && isWebsocketStatusMessage(value),
    format: (value, serialize) => serialize((value as { statusMessage: unknown }).statusMessage),
  });
}
